import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, HeartHandshake, Heart, Bell, Calendar, Inbox, CheckCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Section, List } from "@/components/orio";

const TYPE_META = {
  emergency: { color: "#FF3B30", icon: AlertTriangle, label: "Emergenza" },
  mission: { color: "#FFB020", icon: HeartHandshake, label: "Missione" },
  match: { color: "#34D399", icon: Heart, label: "Match" },
  reminder: { color: "#FFB020", icon: Calendar, label: "Promemoria" },
  default: { color: "#9aa0a6", icon: Bell, label: "Notifica" },
};

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    setItems((arr) => arr.map((n) => (n.id === id ? { ...n, read: true } : n)));
    try { await api.post(`/notifications/${id}/read`); } catch {}
  };

  const markAllRead = async () => {
    setItems((arr) => arr.map((n) => ({ ...n, read: true })));
    try { await api.post("/notifications/read-all"); toast.success("Tutte le notifiche segnate come lette"); }
    catch { toast.error("Non riuscito, riprova"); }
  };

  const clearAll = async () => {
    if (!window.confirm("Eliminare tutte le notifiche? L'azione non si annulla.")) return;
    const prev = items;
    setItems([]);
    try { await api.delete("/notifications"); toast.success("Notifiche svuotate"); }
    catch { setItems(prev); toast.error("Non riuscito, riprova"); }
  };

  const deleteOne = async (id) => {
    const prev = items;
    setItems((arr) => arr.filter((n) => n.id !== id));
    try { await api.delete(`/notifications/${id}`); }
    catch { setItems(prev); toast.error("Non riuscito, riprova"); }
  };

  const filtered = items.filter((n) => filter === "all" || n.type === filter || (filter === "unread" && !n.read));
  const unreadCount = items.filter((n) => !n.read).length;

  const tabs = [
    { id: "all", label: "Tutte" },
    { id: "unread", label: `Non lette · ${unreadCount}` },
    { id: "emergency", label: "Emergenze" },
    { id: "mission", label: "Missioni" },
    { id: "match", label: "Match" },
  ];

  // Group by relative day
  const groups = groupByDay(filtered);

  return (
    <div className="relative max-w-3xl mx-auto px-4 sm:px-6 py-8 pb-32">
      <div className="text-[10px] uppercase tracking-[0.22em] text-[#FFB020] mb-2">Centro notifiche</div>
      <h1 className="font-display text-3xl sm:text-4xl tracking-tight leading-tight">
        Tutto quello che <span className="orio-grad-text">è successo</span>
      </h1>
      <p className="text-[#6B6B6B] mt-2 text-sm max-w-xl">
        Emergenze, missioni, match, promemoria sanitari — in ordine cronologico.
      </p>

      {/* Bulk actions — visible only if there's at least one notification */}
      {items.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={markAllRead}
            disabled={unreadCount === 0}
            data-testid="notif-mark-all-read"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium border transition-colors disabled:opacity-40"
            style={{ background: "#FAF7F0", borderColor: "#F0EBE3", color: "#1A1A1A" }}
          >
            <CheckCheck className="h-3.5 w-3.5" /> Segna tutte lette
          </button>
          <button
            type="button"
            onClick={clearAll}
            data-testid="notif-clear-all"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium border transition-colors"
            style={{ background: "#FEF2F2", borderColor: "#FCA5A5", color: "#B91C1C" }}
          >
            <Trash2 className="h-3.5 w-3.5" /> Svuota tutto
          </button>
        </div>
      )}

      {/* Filter pills */}
      <div className="mt-6 flex gap-2 overflow-x-auto no-scrollbar pb-1 -mx-1 px-1">
        {tabs.map((t) => (
          <button
            key={t.id}
            data-testid={`notif-tab-${t.id}`}
            onClick={() => setFilter(t.id)}
            className={`px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all border ${
              filter === t.id
                ? "text-white border-transparent shadow-[0_6px_18px_-4px_rgba(255,122,26,0.45)]"
                : "bg-[#FAF7F0] text-[#6B6B6B] hover:text-[#1A1A1A] border-[#F0EBE3]"
            }`}
            style={
              filter === t.id
                ? { background: "linear-gradient(135deg, #FF7A1A 0%, #FF5C2A 100%)" }
                : undefined
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-[#9A9A9A] text-sm py-16 text-center">Caricamento…</div>
      ) : filtered.length === 0 ? (
        <div className="mt-10 rounded-3xl border border-[#F0EBE3] p-10 text-center bg-white shadow-[0_8px_30px_-10px_rgba(0,0,0,0.06)]">
          <div className="h-12 w-12 rounded-2xl mx-auto bg-[#F4EFE6] flex items-center justify-center mb-3">
            <Inbox className="h-5 w-5 text-[#9A9A9A]" />
          </div>
          <div className="text-[#1A1A1A] text-sm">Nessuna notifica in questa categoria.</div>
          <div className="text-xs text-[#9A9A9A] mt-1">Tornerà qui appena succederà qualcosa.</div>
        </div>
      ) : (
        groups.map(([dayLabel, dayItems]) => (
          <Section key={dayLabel} eyebrow={dayLabel} title={null} className="mt-7">
            <List>
              {dayItems.map((n) => {
                const meta = TYPE_META[n.type] || TYPE_META.default;
                const Icon = meta.icon;
                const inner = (
                  <div className="w-full text-left flex items-start gap-3 px-4 sm:px-5 py-3.5 hover:bg-[#FAF7F0] active:bg-white/[0.05] transition-colors relative">
                    <div
                      className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
                      style={{
                        background: `${meta.color}1F`,
                        color: meta.color,
                        border: `1px solid ${meta.color}26`,
                      }}
                    >
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0 pr-8">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] uppercase tracking-[0.18em]" style={{ color: meta.color }}>
                          {meta.label}
                        </span>
                        <span className="text-[10px] text-[#9A9A9A]">·</span>
                        <span className="text-[10px] text-[#9A9A9A]">{relative(n.created_at)}</span>
                        {!n.read && (
                          <span className="ml-auto h-2 w-2 rounded-full shrink-0" style={{ background: meta.color, boxShadow: `0 0 8px ${meta.color}` }} />
                        )}
                      </div>
                      <div className={`mt-1 text-[15px] leading-snug ${n.read ? "text-[#1A1A1A]" : "text-[#1A1A1A]"}`}>
                        {n.title}
                      </div>
                      {n.body && (
                        <div className="text-[13px] text-[#9A9A9A] mt-0.5 line-clamp-2">{n.body}</div>
                      )}
                    </div>
                    {/* Delete X — absolute so it doesn't nest inside the Link. */}
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); deleteOne(n.id); }}
                      aria-label="Elimina notifica"
                      data-testid={`notif-delete-${n.id}`}
                      className="absolute top-3 right-3 h-7 w-7 rounded-full flex items-center justify-center text-[#9A9A9A] hover:text-[#B91C1C] hover:bg-[#FEF2F2] transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
                return n.link ? (
                  <Link
                    key={n.id}
                    to={n.link}
                    onClick={() => markRead(n.id)}
                    data-testid={`notif-${n.id}`}
                    className="block"
                  >
                    {inner}
                  </Link>
                ) : (
                  <button
                    key={n.id}
                    onClick={() => markRead(n.id)}
                    data-testid={`notif-${n.id}`}
                    className="w-full block"
                  >
                    {inner}
                  </button>
                );
              })}
            </List>
          </Section>
        ))
      )}
    </div>
  );
}

function groupByDay(items) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today.getTime() - 86400000);
  const weekAgo = new Date(today.getTime() - 7 * 86400000);
  const groups = new Map();
  for (const n of items) {
    const d = new Date(n.created_at || Date.now());
    const dn = new Date(d);
    dn.setHours(0, 0, 0, 0);
    let label;
    if (dn.getTime() === today.getTime()) label = "Oggi";
    else if (dn.getTime() === yesterday.getTime()) label = "Ieri";
    else if (dn >= weekAgo) label = "Questa settimana";
    else label = d.toLocaleDateString("it-IT", { month: "long", year: "numeric" });
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label).push(n);
  }
  return Array.from(groups.entries());
}

function relative(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "ora";
  if (diff < 3600) return `${Math.floor(diff / 60)} min fa`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h fa`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)} g fa`;
  return d.toLocaleDateString("it-IT");
}
